import React, { useEffect, useState } from 'react';
import './SeoEnricherSections.css';
import { SeoEnricherSection } from '../../utils/seoEnricherPreview';
import {
  getSeoEnricherIntentColor,
  isSeoEnricherIntent,
  SEO_ENRICHER_DEFAULT_INTENT_COLOR
} from '../../constants/seoEnricherIntents';
import { getAppEnv } from '../../../config/env';

import { getSeoEnricherIntentIconPath } from './intentIconPaths';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

type SeoEnricherSectionsProps = {
  sourceCityName?: string;
  destinationCityName?: string;
  /**
   * Pre-resolved sections (token replacement / source selection happens in
   * the parent, e.g. RouteTemplate).
   */
  sections?: SeoEnricherSection[];
};

const SeoEnricherSections = ({
  sourceCityName,
  destinationCityName,
  sections
}: SeoEnricherSectionsProps) => {
  const previewSections = sections && sections.length ? sections : [];

  // On SSR (and the first hydration render) isClient is false, so all content
  // is rendered inline & visible. After mount it flips to false-by-default,
  // collapsing the content on the client (shown via modal on click).
  const [isClient, setIsClient] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!previewSections.length) return null;

  const title =
    sourceCityName && destinationCityName
      ? `${sourceCityName} to ${destinationCityName} Cab Booking Guide on MakeMyTrip`
      : 'Cab Booking Guide on MakeMyTrip';

  const activeSection =
    activeIdx !== null ? previewSections[activeIdx] : undefined;

  const openSection = (idx: number) => setActiveIdx(idx);

  const handleItemKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    idx: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openSection(idx);
    }
  };

  return (
    <section className="inclExcl">
      <div className="inclExcl__header">
        <h2 className="inclExcl__title">{title}</h2>
      </div>

      <div className="inclExcl__grid">
        {previewSections.map((section, idx) => {
          const resolvedIntent =
            section.intent && isSeoEnricherIntent(section.intent)
              ? section.intent
              : null;
          const intentColor = resolvedIntent
            ? getSeoEnricherIntentColor(resolvedIntent)
            : SEO_ENRICHER_DEFAULT_INTENT_COLOR;
          const intentIconPath = getSeoEnricherIntentIconPath(resolvedIntent);
          const intentIconUrl = `${PUBLIC_URL}/static/public${intentIconPath}`;
          const isClickable = isClient && Boolean(section.content);
          return (
            <div
              className={`inclExcl__item${
                isClickable ? ' inclExcl__item--clickable' : ''
              }`}
              key={`item-${idx}`}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              aria-label={
                isClickable ? `More about ${section.title}` : undefined
              }
              onClick={isClickable ? () => openSection(idx) : undefined}
              onKeyDown={
                isClickable ? event => handleItemKeyDown(event, idx) : undefined
              }
            >
              <div className="inclExcl__itemMain">
                <span
                  className="inclExcl__intentIcon"
                  style={{
                    backgroundColor: `${intentColor}14`,
                    borderColor: `${intentColor}33`
                  }}
                  aria-hidden="true"
                >
                  <span
                    className="inclExcl__intentImage"
                    style={{
                      backgroundColor: intentColor,
                      WebkitMaskImage: `url("${intentIconUrl}")`,
                      maskImage: `url("${intentIconUrl}")`
                    }}
                  />
                </span>
                <div className="inclExcl__itemBody">
                  <p className="inclExcl__itemTitle">{section.title}</p>
                  <p className="inclExcl__itemSubtitle">{section.subtitle}</p>
                </div>
                <span className="inclExcl__infoBtn" aria-hidden="true">
                  i
                </span>
              </div>
              {/* SSR / pre-hydration: content is rendered inline for SEO. */}
              {!isClient && section.content ? (
                <div
                  className="inclExcl__infoBox"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Client-only: clicking a section card opens the content in a modal. */}
      {isClient && activeSection && activeSection.content ? (
        <div
          className="inclExcl__modalOverlay"
          onClick={() => setActiveIdx(null)}
        >
          <div
            className="inclExcl__modal"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <div className="inclExcl__modalHeader">
              <div>
                <h3 className="inclExcl__modalTitle">{activeSection.title}</h3>
                {activeSection.subtitle ? (
                  <p className="inclExcl__modalSubtitle">
                    {activeSection.subtitle}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="inclExcl__modalClose"
                aria-label="Close"
                onClick={() => setActiveIdx(null)}
              >
                ×
              </button>
            </div>
            <div
              className="inclExcl__modalBody"
              dangerouslySetInnerHTML={{ __html: activeSection.content }}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SeoEnricherSections;
